import React from 'react'
import { Button, Col, Form, InputNumber, Row, Select, Skeleton, Space, Typography, Divider, Card, message, Input } from 'antd'
import { FormComponent } from '../../../BaseComponent'
import { ShelterContextData } from '../../../../context/ShelterContext'
import { ShelterSettingContextData } from '../../../../context/ShelterSettingContext'
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { PetAgeArray, PetSexArray, PetSizeArray } from '../../../../interfaces/IPet'
import { SpecieContextData } from '../../../../context/SpecieContext'
import { BreedContextData } from '../../../../context/BreedContext'
import ILimitControlMatch from '../../../../interfaces/ILimitControlMatch'
import { ShelterSettingAdoptionSaveRequest } from '../../../../services/api/interfaces/ApiRequest'
import IShelterAdoptionLimit from '../../../../interfaces/IShelterAdoptionLimit'

type LimitControlProps = {
  uuid: string

  context: ShelterContextData
  settingsContext: ShelterSettingContextData
  specieContext: SpecieContextData
  breedContext: BreedContextData
}

export default class LimitControl extends FormComponent<LimitControlProps> {

  private readonly SUPPORTED_FIELDS = [
    {
      label: 'Age',
      value: 'age_key'
    },
    {
      label: 'Sex',
      value: 'sex_key'
    },
    {
      label: 'Size',
      value: 'size_key'
    },
    {
      label: 'Specie',
      value: 'specie_id'
    },
    {
      label: 'Primary Breed',
      value: 'breed_primary_id'
    },
    {
      label: 'Secondary Breed',
      value: 'secondary_breed_id'
    },
    {
      label: 'Foster',
      value: 'foster',
    }
  ]

  state = {
    loaded: false,
    initialValues: {} as any,
    fieldValues: {} as any
  }

  async componentDidMount() {
    const { uuid, settingsContext, specieContext, breedContext } = this.props

    const options = {
      _force_ids: true,
      limit:99999
    }

    if (uuid && !settingsContext.fetching) {
      await settingsContext.requestShelterSettingList({
        search: {
          organization_shelter: {
            has: {
              uuid
            }
          }
        }
      })
    }

    if (!specieContext.fetching && !specieContext.species) {
      await specieContext.requestSpecieList({
        options
      })
    }

    if (!breedContext.fetching && !breedContext.breeds) {
      await breedContext.requestBreedList({
        options
      })
    }

    this.setState({
      loaded: true,
      initialValues: this.getValues()
    })
  }

  private getValues = () => {
    const { settingsContext } = this.props

    const values = settingsContext.shelterSettings?.find(v => v.type_key === 'ADOPTION')?.value as IShelterAdoptionLimit
    if (values) {
      values.pet?.forEach((v, index) => {
        if (!!v.match) {
          v.match = Object.keys(v.match).map((k, idx) => {
            const fieldName = ['pet', index, 'match', idx, 'type_key'].join('.')
            this.state.fieldValues[fieldName] = this.getCurrentTypeOptions(k)

            return {
              type_key: k,
              value: !!v.match ? v.match[k] : undefined
            }
          }) as any
        }
      })
    }

    return values
  }

  private getCurrentTypeOptions = (type: string) => {
    const { specieContext, breedContext } = this.props

    const enumOptions = (value: any) => {
      return {
        value 
      }
    }

    if (type === 'age_key') {
      return PetAgeArray.map(enumOptions)
    }
    if (type === 'sex_key') {
      return PetSexArray.map(enumOptions)
    }
    if (type === 'size_key') {
      return PetSizeArray.map(enumOptions)
    }
    if (type === 'specie_id') {
      return specieContext.species?.map(p => {
        return {
          label: p.name,
          value: p.id
        }
      })
    }
    if (type === 'breed_primary_id' || type === 'secondary_breed_id') {
      return breedContext.breeds?.map(p => {
        return {
          label: p.name,
          value: p.id
        }
      })
    }
    if (type === 'foster') {
      return [{ label: 'True', value: true }, { label: 'False', value: false }]
    }

    return []
  }

  private handleSave = async (values: any) => {
    const { uuid, settingsContext } = this.props

    try {
      const adopterLimits = values.adopter

      const petLimits: ILimitControlMatch[] = values.pet?.map(v => {
        const match = {}
        v.match?.forEach(p => {
          match[p.type_key] = p.value
        })

        return {
          ...v,
          match
        }
      })

      const payload: ShelterSettingAdoptionSaveRequest = {
        shelter_uuid: uuid,
        type_key: 'ADOPTION',
        value: {
          adopter: adopterLimits?.limit?.max && adopterLimits?.limit?.max ? adopterLimits : null,
          pet: petLimits?.length > 0 ? petLimits : null,
          cta: values.cta
        }
      }

      await settingsContext.requestShelterSettingAdoptionSave(payload)
      message.success('Limit control saved successfully!')
    } catch (error) {
      message.error(`Failed to save limit contorl. Reason: ${error}`)
    }
  }

  private handleChange = async (values: any[], allValues?: any[]) => {
    const { fieldValues } = this.state

    for await (const v of values) {
      if (v.name.includes('type_key') && !!v.value) {
        const fieldName = v.name.join('.')

        fieldValues[fieldName] = this.getCurrentTypeOptions(v.value)
      }
    }
    this.setState({
      fieldValues
    })
  }

  private _renderPetMatches = (name: any) => {
    return (
      <Form.List name={[name, 'match']}>
        {(fields, { add, remove }) => {
          return (
            <>
              {
                fields.map(field => {
                  const fieldName = ['pet', name, 'match', field.name, 'type_key'].join('.')
                  const fieldValue = this.state.fieldValues[fieldName]

                  return (
                    <Row key={field.key} gutter={24}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          {...field}
                          label="Type"
                          name={[field.name, 'type_key']}
                          fieldKey={[field.fieldKey, 'type_key']}
                          rules={[
                            {
                              required: true,
                              message: 'You must select a type'
                            }
                          ]}
                        >
                          <Select
                            placeholder="Select a type please"
                            options={this.SUPPORTED_FIELDS}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12}>
                        <Space direction="horizontal">
                          {
                            !!fieldValue ? (
                              <Form.Item
                                {...field}
                                label="Type"
                                name={[field.name, 'value']}
                                fieldKey={[field.fieldKey, 'value']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'You must select a value'
                                  }
                                ]}
                              >
                                <Select
                                  placeholder="Select a value"
                                  options={fieldValue}
                                  allowClear={true}
                                  showSearch={true}
                                  filterOption={(input, option) => {
                                    if (!option?.label || typeof option.label !== 'string') return false
                          
                                    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                  }}
                                  style={{minWidth: 150}}
                                />
                              </Form.Item>
                            ) : null
                          }

                          <Button
                            type="dashed"
                            shape="circle"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                            style={{
                              marginTop: !fieldValue ? 28 : 0
                            }}  
                          />
                        </Space>
                      </Col>
                    </Row>
                  )
                })
              }

              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  style={{
                    marginTop: fields.length > 0 ? 0 : 30
                  }}
                  block
                >
                  <PlusCircleOutlined /> Add combination
                </Button>
              </Form.Item>
            </>
          )
        }}
      </Form.List>
    )
  }

  private _renderPetLimit = () => {
    return (
      <Form.List name="pet">
        {(fields, { add, remove }) => {
          const removeBtn = (field) => (
            <Button
              type="dashed"
              shape="circle"
              icon={<MinusCircleOutlined />}
              onClick={() => remove(field.name)}
            />
          )

          return (
            <>
              {
                fields.map(field => (
                  <Card
                    key={field.key}
                    title={`Limit ${field.key + 1} of ${fields.length}`}
                    style={{marginBottom: 8}}
                    extra={removeBtn(field)}
                  >
                    <Row gutter={24}>
                      <Col xs={24} md={8}>
                        <Typography.Title level={5}>Values</Typography.Title>

                        <Space direction="horizontal">
                          <Form.Item
                            {...field}
                            label="Min"
                            name={[field.name, 'limit', 'min']}
                            fieldKey={[field.fieldKey, 'limit', 'min']}
                            rules={[
                              {
                                required: true,
                                message: 'You must inform a minimum value otherwise remove this limit'
                              }
                            ]}
                          >
                            <InputNumber min={0} />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            label="Max"
                            name={[field.name, 'limit', 'max']}
                            fieldKey={[field.fieldKey, 'limit', 'max']}
                            rules={[
                              {
                                required: true,
                                message: 'You must inform a maximum value otherwise remove this limit'
                              }
                            ]}
                          >
                            <InputNumber min={0} />
                          </Form.Item>
                        </Space>
                      </Col>

                      <Col xs={24} md={16}>
                        <Typography.Title level={5}>Combinations</Typography.Title>

                        {this._renderPetMatches(field.name)}
                      </Col>
                    </Row>
                  </Card>
                ))
              }

              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                >
                  <PlusCircleOutlined /> Add pet limit
                </Button>
              </Form.Item>
            </>
          )
        }}
      </Form.List>
    )
  }

  render() {
    const { loaded, initialValues } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    return (
      <div>
        <Form
          name="limit_control"
          layout="vertical"
          initialValues={initialValues}
          ref={(refs: any) => this.formRef = refs}
          onFinish={this.handleSave}
          onFieldsChange={this.handleChange}
          style={{width: '100%'}}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>

            <Typography.Title level={5}>Adopt me CTA</Typography.Title>
            <Typography.Text>Customize the text of the Adopt Me button. When empty, it defaults to “Adopt Me”</Typography.Text>
            <br/><br/>
              <Space direction="horizontal">
                <Form.Item
                  label=""
                  name={['cta']}
                >
                  <Input />
                </Form.Item>
              </Space>

              <Typography.Title level={5}>Adopter Limits</Typography.Title>
              <Typography.Text>Limits the number of active adoption inquiries an adopter can have at a time for this shelter. In case the adopter tries to submit any number of inquiries beyond this limit, it will prompt them to withdraw one of the existing inquiries in order to submit the new one. The active adoption inquiries are: Pending, Under Review, Approved, Signed, Paid.</Typography.Text>
              <br/><br/>
              <Space direction="horizontal">
                <Form.Item
                  label="Min"
                  name={['adopter', 'limit', 'min']}
                >
                  <InputNumber min={0} />
                </Form.Item>

                <Form.Item
                  label="Max"
                  name={['adopter', 'limit', 'max']}
                >
                  <InputNumber min={0} />
                </Form.Item>
              </Space>

              <Typography.Title level={5}>Pet Limits</Typography.Title>
              <Typography.Text>When a pet reaches the Maximum number of active adoption inquiries, it will become RESTRICTED. When Restricted, pets are automatically hidden from shelter listing pages and iframes. Then, if the pet reaches the Minimum limit of active adoption inquiries, the animal will automatically be listed again. The active adoption inquiries are Pending, Under Review, Approved, Signed, and Paid.</Typography.Text>
              <Divider />

              {this._renderPetLimit()}
              
            </Col>

          </Row>

        </Form>
      </div>
    )
  }
}
